import { useState } from "react";

export default function LandingPage() {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredFeature, setHoveredFeature] = useState(null);

  const steps = [
    {
      icon: "📤",
      step: "Step 1",
      title: "Upload your scan",
      body: "Drop any X-ray, MRI, or skin photo. Supports JPEG and PNG.",
    },
    {
      icon: "🤖",
      step: "Step 2",
      title: "AI reads it instantly",
      body: "Our AI model analyzes every detail of your scan in seconds.",
    },
    {
      icon: "📋",
      step: "Step 3",
      title: "Get clear answers",
      body: "Plain English explanation, urgency level, and the right doctor to call.",
    },
  ];

  const features = [
    {
      icon: "🔍",
      title: "Honest, not scary",
      body: "We tell you the truth clearly — serious findings get flagged immediately with what to do next.",
    },
    {
      icon: "👨‍⚕️",
      title: "22 Valley specialists",
      body: "Direct phone numbers of top doctors in Srinagar — matched to your specific condition.",
    },
    {
      icon: "🌐",
      title: "Any scan type",
      body: "X-rays, MRIs, CT scans, skin conditions and medical reports all supported.",
    },
    {
      icon: "🚨",
      title: "Emergency guidance",
      body: "If something looks urgent, we tell you what to do right now before reaching a hospital.",
    },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>

      {/* ── NAVBAR ── */}
      <nav style={{
        position: "fixed",
        top: 0, left: 0, right: 0,
        height: 64,
        background: "rgba(255,255,255,0.95)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid #f1f5f9",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 5%",
      }}>
        {/* Logo left */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img
            src="/assets/logo.svg"
            alt="RepoScan"
            style={{ height: 36, width: "auto" }}
          />
          <span style={{ fontWeight: 700, fontSize: 18, color: "#0f172a" }}>
            RepoScan
          </span>
        </div>

        {/* Button right */}
        <a href="/analyze" style={{
          background: "#2563eb",
          color: "white",
          borderRadius: 999,
          padding: "10px 24px",
          fontWeight: 600,
          fontSize: 14,
          textDecoration: "none",
          display: "inline-block",
        }}>
          Analyze Scan →
        </a>
      </nav>

      {/* ── HERO ── */}
      <section style={{
        minHeight: "100vh",
        background: "#f8fafc",
        position: "relative",
        overflow: "hidden",
        paddingTop: 80,
      }}>
        {/* Dot grid background */}
        <div style={{
          position: "absolute",
          inset: 0,
          backgroundImage: "radial-gradient(circle, #cbd5e1 1px, transparent 1px)",
          backgroundSize: "28px 28px",
          opacity: 0.5,
          zIndex: 0,
        }} />

        {/* Hero content */}
        <div
          className="hero-content"
          style={{
            position: "relative",
            zIndex: 1,
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 5%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: "calc(100vh - 80px)",
            gap: 48,
          }}
        >
          {/* Left text column */}
          <div style={{ flex: 1, maxWidth: 520 }}>

            {/* Badge */}
            <div style={{
              display: "inline-block",
              background: "#eff6ff",
              color: "#2563eb",
              borderRadius: 999,
              padding: "6px 16px",
              fontSize: 13,
              fontWeight: 500,
              marginBottom: 24,
            }}>
              🔬 AI-Powered Medical Analysis
            </div>

            {/* Heading */}
            <h1 style={{
              fontSize: "clamp(40px, 6vw, 64px)",
              fontWeight: 800,
              lineHeight: 1.1,
              color: "#0f172a",
              margin: "0 0 20px 0",
            }}>
              Your Scan.<br />
              <span style={{ color: "#2563eb" }}>Explained Simply.</span>
            </h1>

            {/* Subtext */}
            <p style={{
              fontSize: 18,
              color: "#64748b",
              lineHeight: 1.6,
              maxWidth: 460,
              margin: "0 0 32px 0",
            }}>
              Upload any X-ray, MRI, or skin photo and get a clear,
              honest AI second opinion — plus the right specialist
              to call in Kashmir.
            </p>

            {/* Buttons */}
            <div className="hero-buttons" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a href="/analyze" style={{
                background: "#2563eb",
                color: "white",
                borderRadius: 999,
                padding: "14px 32px",
                fontWeight: 600,
                fontSize: 16,
                textDecoration: "none",
                boxShadow: "0 4px 24px rgba(37,99,235,0.25)",
                display: "inline-block",
              }}>
                Analyze My Scan →
              </a>
              <a href="#how-it-works" style={{
                background: "transparent",
                color: "#475569",
                borderRadius: 999,
                padding: "14px 32px",
                fontWeight: 500,
                fontSize: 16,
                textDecoration: "none",
                border: "1.5px solid #e2e8f0",
                display: "inline-block",
              }}>
                How it works ↓
              </a>
            </div>

            {/* Trust badges */}
            <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 20 }}>
              🔒 No data stored · 🆓 Free to use · 🏥 22 Kashmir doctors
            </p>
          </div>

          {/* Right phone column */}
          <div style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}>
            <img
              src="/assets/Phone.png"
              alt="RepoScan App"
              className="hero-phone"
              style={{
                width: "min(360px, 90%)",
                animation: "float 4s ease-in-out infinite",
                filter: "drop-shadow(0 40px 80px rgba(0,0,0,0.18))",
              }}
            />
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" style={{ background: "#ffffff", padding: "100px 5%" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h2 style={{
            fontSize: 36, fontWeight: 700, color: "#0f172a",
            textAlign: "center", marginBottom: 12,
          }}>
            How RepoScan Works
          </h2>
          <p style={{
            fontSize: 16, color: "#64748b",
            textAlign: "center", marginBottom: 60,
          }}>
            Three steps to understanding your scan
          </p>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 24,
          }}>
            {steps.map((s, i) => (
              <div
                key={i}
                onMouseEnter={() => setHoveredCard(i)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  background: "#fff",
                  border: "1px solid #f1f5f9",
                  borderRadius: 20, padding: "36px 28px",
                  boxShadow: hoveredCard === i
                    ? "0 8px 32px rgba(0,0,0,0.10)"
                    : "0 2px 16px rgba(0,0,0,0.05)",
                  transform: hoveredCard === i ? "translateY(-4px)" : "translateY(0)",
                  transition: "all 0.25s ease",
                  cursor: "default",
                }}
              >
                <div style={{ fontSize: 40 }}>{s.icon}</div>
                <span style={{
                  display: "inline-block",
                  background: "#f1f5f9", color: "#64748b",
                  fontSize: 11, borderRadius: 999,
                  padding: "3px 10px", margin: "12px 0",
                }}>
                  {s.step}
                </span>
                <h3 style={{ fontWeight: 700, fontSize: 18, margin: "0 0 8px 0", color: "#0f172a" }}>
                  {s.title}
                </h3>
                <p style={{ color: "#64748b", fontSize: 14, margin: 0, lineHeight: 1.6 }}>
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section style={{ background: "#f8fafc", padding: "100px 5%" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h2 style={{ fontSize: 36, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>
            Built for real people in Kashmir
          </h2>
          <p style={{ fontSize: 16, color: "#64748b", marginBottom: 60 }}>
            No medical degree needed to understand your results
          </p>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 20,
          }}>
            {features.map((f, i) => (
              <div
                key={i}
                onMouseEnter={() => setHoveredFeature(i)}
                onMouseLeave={() => setHoveredFeature(null)}
                style={{
                  background: "#fff",
                  borderRadius: 16, padding: 28,
                  border: "1px solid #f1f5f9",
                  boxShadow: hoveredFeature === i
                    ? "0 8px 32px rgba(0,0,0,0.08)"
                    : "0 2px 8px rgba(0,0,0,0.03)",
                  transform: hoveredFeature === i ? "translateY(-2px)" : "none",
                  transition: "all 0.2s ease",
                }}
              >
                <div style={{ fontSize: 32, marginBottom: 12 }}>{f.icon}</div>
                <h3 style={{ fontWeight: 700, fontSize: 16, margin: "0 0 8px 0", color: "#0f172a" }}>
                  {f.title}
                </h3>
                <p style={{ color: "#64748b", fontSize: 14, margin: 0, lineHeight: 1.6 }}>
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{
        background: "linear-gradient(135deg, #1e40af, #2563eb)",
        padding: "100px 5%", textAlign: "center", color: "#fff",
      }}>
        <h2 style={{ fontSize: 40, fontWeight: 800, marginBottom: 16 }}>
          Get a second opinion. Right now.
        </h2>
        <p style={{ fontSize: 18, opacity: 0.85, marginBottom: 36 }}>
          Free. Instant. Designed for Kashmir.
        </p>
        <a href="/analyze" style={{ textDecoration: "none" }}>
          <button
            style={{
              background: "#fff", color: "#2563eb",
              borderRadius: 999, padding: "16px 40px",
              fontWeight: 700, fontSize: 16, border: "none",
              cursor: "pointer",
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 8px 36px rgba(0,0,0,0.22)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 24px rgba(0,0,0,0.15)";
            }}
          >
            Analyze My Scan →
          </button>
        </a>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{
        background: "#0f172a",
        padding: "40px 5%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 16,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src="/assets/logo.svg" height="24" alt="RepoScan" />
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>RepoScan</span>
        </div>
        <span style={{ fontSize: 12, color: "#475569" }}>
          Not a substitute for professional medical advice
        </span>
        <span style={{ fontSize: 12, color: "#475569" }}>
          Made with ❤️ for Kashmir
        </span>
      </footer>

      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .hero-content {
            flex-direction: column !important;
            text-align: center !important;
            padding-top: 100px !important;
          }
          .hero-phone {
            width: 240px !important;
            margin-top: 32px;
          }
          .hero-buttons {
            justify-content: center !important;
          }
        }
      `}</style>
    </div>
  );
}
